<template>
<v-container fluid grid-list-lg>
  <v-layout row wrap>
    <v-flex>
      <v-card>
        <v-card-title primary-title class="primary white--text headline">
          Topic Model Statistics
        </v-card-title>
        <v-tabs
        grow
        icons-and-text
        dark
        slider-color="grey lighten-4"
        color="primary"
        v-model="step">
          <v-tab ripple>
            Model Performance
          </v-tab>
          <v-tab ripple :disabled="isNew">
            Training History
          </v-tab>
          <v-tab ripple :disabled="isNew">
            Evaluation History
          </v-tab>
        </v-tabs>
        <v-card-text>
          <v-tabs-items v-model="step">
            <v-tab-item class="mt-4">
              <v-layout row>
                <v-flex xs12>
                  <v-card>
                    <v-toolbar flat color="yellow darken-4" class="white--text">
                      <div class="headline">Accuracy Metrics</div>
                    </v-toolbar>
                    <v-divider></v-divider>
                    <v-data-table
                    :rows-per-page-items="[10,25,50,100,{'text':'$vuetify.dataIterator.rowsPerPageAll','value':-1}]"
                    :headers="statisticsTableHeaders"
                    :items="accuracyMetrics">
                      <template slot="items" slot-scope="props">
                        <td>{{ props.item.topic_name }}</td>
                        <td>{{ topicModelTypes[props.item.model_type] }}</td>
                        <td>{{ props.item.test_data_size }}</td>
                        <td>{{ props.item.precision | formatPercentage }}</td>
                        <td>{{ props.item.recall | formatPercentage }}</td>
                        <td>{{ props.item.f1_score | formatPercentage }}</td>
                      </template>
                    </v-data-table>
                  </v-card>
                </v-flex>
              </v-layout>
            </v-tab-item>
            <v-tab-item class="mt-4">
              <v-layout row>
                <v-flex xs12>
                  <v-card>
                    <v-toolbar flat color="yellow darken-4" class="white--text">
                      <div class="headline">Training History</div>
                    </v-toolbar>
                    <v-divider></v-divider>
                    <v-data-table
                    :rows-per-page-items="[10,25,50,100,{'text':'$vuetify.dataIterator.rowsPerPageAll','value':-1}]"
                    :headers="trainingHistoryTableHeaders"
                    :items="trainingHistory">
                      <template slot="items" slot-scope="props">
                        <tr @click="props.expanded = !props.expanded">
                          <td>{{ props.item.annotation_work.name }}</td>
                          <td>{{ props.item.created_date | formatDate }}</td>
                          <td>{{ props.item.duration | formatDuration }}</td>
                          <td>{{ props.item.num_instances }}</td>
                          <td>{{ props.item.improved_topics.length }}</td>
                          <td>{{ props.item.worsened_topics.length }}</td>
                          <td>{{ props.item.performance.avg_f_score | formatPercentage }}</td>
                          <td class="text-xs-right">
                            <v-tooltip bottom color="success">
                              <v-icon
                                class="mr-2"
                                color="success"
                                slot="activator"
                                :disabled="!(props.item.performance.topic_performances && props.item.performance.topic_performances.length > 0)"
                                @click="performanceDetailDialog = true; performanceDetails = props.item.performance.topic_performances; props.expanded = !props.expanded"
                              >
                                insert_chart
                              </v-icon>
                              <span>Show Performance Detail</span>
                            </v-tooltip>
                          </td>
                        </tr>
                      </template>
                      <template slot="expand" slot-scope="props">
                        <v-card flat v-if="props.item.improved_topics.length > 0">
                          <v-layout row>
                            <v-flex xs6>
                              <v-card-title>
                                <v-subheader>
                                  Improved Topics
                                </v-subheader>
                              </v-card-title>
                              <v-card-text>
                                <v-list>
                                  <template v-for="(item, index) in props.item.improved_topics">
                                    <v-list-tile
                                      :key="index"
                                    >
                                      <v-list-tile-content>
                                        <v-list-tile-title>{{item.name}}</v-list-tile-title>
                                        <v-list-tile-sub-title></v-list-tile-sub-title>
                                      </v-list-tile-content>
                                    </v-list-tile>
                                  </template>
                                </v-list>
                              </v-card-text>
                            </v-flex>
                            <v-flex xs6>
                              <v-card-title>
                                <v-subheader>
                                  Worsened Topics
                                </v-subheader>
                              </v-card-title>
                              <v-card-text>
                                <v-list>
                                  <template v-for="(item, index) in props.item.worsened_topics">
                                    <v-list-tile
                                      :key="index"
                                    >
                                      <v-list-tile-content>
                                        <v-list-tile-title>{{item.name}}</v-list-tile-title>
                                        <v-list-tile-sub-title></v-list-tile-sub-title>
                                      </v-list-tile-content>
                                    </v-list-tile>
                                  </template>
                                </v-list>
                              </v-card-text>
                            </v-flex>
                          </v-layout>
                        </v-card>
                        <v-card v-else>
                          <v-card-text>
                            <span class="font-italic">There is no additional information about training</span>
                          </v-card-text>
                        </v-card>
                      </template>
                    </v-data-table>
                  </v-card>
                </v-flex>
              </v-layout>
            </v-tab-item>
            <v-tab-item class="mt-4">
              <v-layout row>
                <v-flex xs12>
                  <v-card>
                    <v-toolbar flat color="yellow darken-4" class="white--text">
                      <div class="headline">Evaluation History</div>
                    </v-toolbar>
                    <v-divider></v-divider>
                    <v-data-table
                    :rows-per-page-items="[10,25,50,100,{'text':'$vuetify.dataIterator.rowsPerPageAll','value':-1}]"
                    :headers="evaluationHistoryTableHeaders"
                    :items="evaluationHistory">
                      <template slot="items" slot-scope="props">
                        <tr @click="props.expanded = !props.expanded">
                          <td>{{ props.item.annotation_work.name }}</td>
                          <td>{{ props.item.model_type }}</td>
                          <td>{{ props.item.created_date | formatDate }}</td>
                          <td>{{ props.item.duration | formatDuration }}</td>
                          <td>{{ props.item.performance.avg_f_score | formatPercentage }}</td>
                          <td class="text-xs-right">
                            <v-tooltip bottom color="success">
                              <v-icon
                                class="mr-2"
                                color="success"
                                slot="activator"
                                :disabled="!(props.item.performance.topic_performances && props.item.performance.topic_performances.length > 0)"
                                @click="performanceDetailDialog = true; performanceDetails = props.item.performance.topic_performances; props.expanded = !props.expanded"
                              >
                                insert_chart
                              </v-icon>
                              <span>Show Performance Detail</span>
                            </v-tooltip>
                          </td>
                        </tr>
                      </template>
                    </v-data-table>
                  </v-card>
                </v-flex>
              </v-layout>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
        <v-card-actions style="background: hsl(0, 0%, 97%)" class="pa-3">
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" flat depressed @click.native="cancel">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-flex>
  </v-layout>
    <v-dialog
    v-model="performanceDetailDialog"
    persistent
    scrollable
    width="1400"
    >
      <v-card>
        <v-card-title primary-title class="primary white--text headline">Performance Details</v-card-title>
          <v-data-table
          :rows-per-page-items="[10,25,50,100,{'text':'$vuetify.dataIterator.rowsPerPageAll','value':-1}]"
          :headers="performanceDetailsTableHeaders"
          :items="performanceDetails">
            <template slot="items" slot-scope="props">
              <td>{{ props.item.id }}</td>
              <td>{{ props.item.created_date | formatDate }}</td>
              <td>{{ props.item.topic.name }}</td>
              <td>{{ props.item.precision | formatPercentage }}</td>
              <td>{{ props.item.recall | formatPercentage }}</td>
              <td>{{ props.item.f_score | formatPercentage }}</td>
            </template>
          </v-data-table>
        <v-card-actions style="background: hsl(0, 0%, 97%)" class="pa-3">
          <v-spacer></v-spacer>
          <v-btn
            color="grey darken-1"
            flat
            depressed
            @click="performanceDetails = []; performanceDetailDialog = false;"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
</v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
  props: {
    id: {
      type: String,
      required: false,
      default: ''
    },
    stepProp: {
      type: Number,
      required: false,
      default: 1
    }
  },
  created () {
    if (!this.isNew) {
      this.setLoader({
        loading: true
      })
    }
    this.fetchTopicModelItem()
  },
  data () {
    return {
      statisticsTableHeaders: [
        {text: 'Topic Name', sortable: true, value: 'topic_name'},
        {text: 'Model Type', sortable: true, value: 'model_type'},
        {text: 'Test Data Size', sortable: true, value: 'test_data_size'},
        {text: 'Precision', sortable: true, value: 'precision'},
        {text: 'Recall', sortable: true, value: 'recall'},
        {text: 'F1 Score', sortable: true, value: 'f1_score'}
      ],
      trainingHistoryTableHeaders: [
        {text: 'Annotation Work', sortable: true, value: 'annotation_work'},
        {text: 'Created Date', sortable: true, value: 'created_date'},
        {text: 'Duration', sortable: true, value: 'duration'},
        {text: 'Training Data Size', sortable: true, value: 'num_instances'},
        {text: 'Improved Topics', sortable: true, value: 'improved_topics'},
        {text: 'Worsened Topics', sortable: true, value: 'worsened_topics'},
        {text: 'CV F1 Score', sortable: true, value: 'performance.avg_f_score'},
        {text: 'Actions', value: 'name', sortable: false, align: 'right'}
      ],
      evaluationHistoryTableHeaders: [
        {text: 'Annotation Work', sortable: true, value: 'annotation_work'},
        {text: 'Model Type', sortable: true, value: 'model_type'},
        {text: 'Created Date', sortable: true, value: 'created_date'},
        {text: 'Duration', sortable: true, value: 'duration'},
        {text: 'F1 Score', sortable: true, value: 'performance.avg_f_score'},
        {text: 'Actions', value: 'name', sortable: false, align: 'right'}
      ],
      performanceDetailsTableHeaders: [
        {text: 'ID', sortable: true, value: 'id'},
        {text: 'Created Date', sortable: true, value: 'created_date'},
        {text: 'Topic', sortable: true, value: 'topic.name'},
        {text: 'Precision', sortable: true, value: 'precision'},
        {text: 'Recall', sortable: true, value: 'recall'},
        {text: 'F1 Score', sortable: true, value: 'f1_score'}
      ],
      item: {
        id: '',
        language: '',
        name: '',
        dataset: '',
        topics: []
      },
      datasets: [],
      accuracyMetrics: [],
      trainingHistory: [],
      evaluationHistory: [],
      saveLoading: false,
      step: 0,
      performanceDetailDialog: false,
      performanceDetails: [],
      topicListLoading: false
    }
  },
  computed: {
    isNew () {
      return this.id == null || this.id === ''
    },
    ...mapState({
      topicModelTypes: state => state.configurations.topicModelTypesDictionary
    })
  },
  watch: {
    id (val) {
      this.item.id = val
    },
    stepProp (val) {
      this.step = val
    }
  },
  methods: {
    cancel () {
      this.$router.push('/topic-models/')
    },
    fetchTopicModelItem () {
      this.axios.get(`/topic-models/${this.id}/stats`)
        .then(response => {
          this.accuracyMetrics = response.data.accuracy_metrics
          this.trainingHistory = response.data.training_history
          this.evaluationHistory = response.data.evaluation_history
          console.log(response.data)
        })
        .catch(error => {
          this.handleAxiosError(error)
        })
        .finally(() => {
          this.setLoader({
            loading: false
          })
        })
    },
    setTopicModelItem (item) {
      this.item = this.mapTopicModelItem(item)
    },
    mapTopicModelItem (item) {
      return {
        id: this.id,
        dataset: item.dataset,
        language: item.language,
        name: item.name,
        topics: item.topics.map((t) => {
          return {
            id: t.id,
            name: t.name,
            positiveSeedwords: t.positive_seedwords,
            negativeSeedwords: t.negative_seedwords,
            positiveInstances: t.positive_instances,
            negativeInstances: t.negative_instances,
            isActive: t.is_active,
            threshold: t.threshold * 100,
            precision: t.precision,
            recall: t.recall,
            fScore: t.f_score,
            matchingMethod: t.matching_method,
            stemming: t.stemming_choice,
            modelPath: t.model_path
          }
        })
      }
    },
    ...mapActions({
      setLoader: 'loader/setLoader'
    })
  }
}
</script>

<style>

</style>
